import React from 'react';
import { Box, Typography } from '@mui/material';

const EMPTY_LABEL = '[empty]';

const formatValue = (value) => {
    if (value === null || value === undefined || value === '') {
        return EMPTY_LABEL;
    }

    return String(value);
};

const isEmptyValue = (value) => value === null || value === undefined || value === '';

export const parseChangeDetails = (raw) => {
    if (!raw) {
        return [];
    }

    try {
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
        return parsed?.Changes || parsed?.changes || [];
    } catch {
        return null;
    }
};

const ScalarChange = ({ change }) => {
    const oldValue = change.OldValue ?? change.oldValue;
    const newValue = change.NewValue ?? change.newValue;

    let detailText;
    if (isEmptyValue(oldValue) && !isEmptyValue(newValue)) {
        detailText = `Set to ${formatValue(newValue)}`;
    } else if (!isEmptyValue(oldValue) && isEmptyValue(newValue)) {
        detailText = `Cleared from ${formatValue(oldValue)}`;
    } else {
        detailText = `${formatValue(oldValue)} → ${formatValue(newValue)}`;
    }

    return (
        <Typography variant="body2" sx={{ color: 'text.primary', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {detailText}
        </Typography>
    );
};

const CollectionChange = ({ change }) => {
    const added = change.Added || change.added || [];
    const removed = change.Removed || change.removed || [];

    return (
        <>
            {removed.map((item, index) => (
                <Typography
                    key={`removed-${index}`}
                    variant="body2"
                    sx={{ color: 'error.main', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                >
                    − {item}
                </Typography>
            ))}
            {added.map((item, index) => (
                <Typography
                    key={`added-${index}`}
                    variant="body2"
                    sx={{ color: 'success.main', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                >
                    + {item}
                </Typography>
            ))}
        </>
    );
};

const DETAIL_COLUMN_MIN_WIDTH = 200;
const DETAIL_COLUMN_MAX_WIDTH = 300;

const ChangeDetailSection = ({ change, fullWidth = false }) => {
    const label = change.DisplayName || change.displayName || change.Field || change.field || 'Change';
    const added = change.Added || change.added || [];
    const removed = change.Removed || change.removed || [];
    const hasCollectionChanges = added.length > 0 || removed.length > 0;

    return (
        <Box
            sx={(theme) => ({
                minWidth: 0,
                width: '100%',
                maxWidth: fullWidth ? '100%' : DETAIL_COLUMN_MAX_WIDTH,
                boxSizing: 'border-box',
                p: 1.5,
                borderRadius: 1,
                border: '1px solid',
                borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'divider',
                backgroundColor: 'background.paper',
                color: 'text.primary'
            })}
        >
            <Typography
                variant="body2"
                sx={{
                    fontWeight: 600,
                    mb: 0.75,
                    wordBreak: 'break-word'
                }}
            >
                {label}
            </Typography>
            <Box sx={{ minWidth: 0 }}>
                {hasCollectionChanges ? (
                    <CollectionChange change={change} />
                ) : (
                    <ScalarChange change={change} />
                )}
            </Box>
        </Box>
    );
};

export const ApprovalAuditChangeDetails = ({ row }) => {
    const changes = parseChangeDetails(row?.ChangeDetails);

    if (changes === null) {
        return (
            <Box sx={{ p: 2 }}>
                <Typography variant="body2" sx={{ color: 'text.primary' }}>
                    Unable to load change details.
                </Typography>
            </Box>
        );
    }

    if (!changes.length) {
        return (
            <Box sx={{ p: 2 }}>
                <Typography variant="body2" sx={{ color: 'text.primary' }}>
                    No field-level changes recorded for this event.
                </Typography>
            </Box>
        );
    }

    const isSingleChange = changes.length === 1;

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: isSingleChange
                    ? '1fr'
                    : `repeat(auto-fill, minmax(${DETAIL_COLUMN_MIN_WIDTH}px, ${DETAIL_COLUMN_MAX_WIDTH}px))`,
                gap: 2,
                p: 2,
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                boxSizing: 'border-box',
                maxHeight: 320,
                overflowY: 'auto',
                overflowX: 'hidden'
            }}
        >
            {changes.map((change, index) => (
                <ChangeDetailSection
                    key={`${change.Field || change.field || change.DisplayName || 'change'}-${index}`}
                    change={change}
                    fullWidth={isSingleChange}
                />
            ))}
        </Box>
    );
};
